import { DatePicker } from "@/components/ui/date-picker";
import {
	Field,
	FieldError,
	FieldGroup,
	FieldLabel,
} from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";

const DOCUMENT_TYPES = [
	{ value: "id_card", label: "Občanský průkaz" },
	{ value: "passport", label: "Pas" },
	{ value: "drivers_license", label: "Řidičský průkaz" },
];

interface Document {
	document_type: string | null;
	document_number: string | null;
	expiry_date: string | null;
	issuer: string | null;
}

interface DocumentErrors {
	document_type?: string;
	document_number?: string;
	expiry_date?: string;
	issuer?: string;
}

interface DocumentFieldGroupProps {
	value: Document;
	onChange: (document: Document) => void;
	idPrefix?: string;
	invalid?: boolean;
	expiryWarning?: boolean;
	expiryExpired?: boolean;
	errors?: DocumentErrors;
	onClearError?: (field: keyof DocumentErrors) => void;
}

function DocumentFieldGroup({
	value,
	onChange,
	idPrefix = "",
	invalid = false,
	expiryWarning = false,
	expiryExpired = false,
	errors,
	onClearError,
}: DocumentFieldGroupProps) {
	const prefix = idPrefix ? `${idPrefix}_` : "";

	return (
		<FieldGroup>
			<div className="grid grid-cols-2 gap-4">
				<Field
					data-warning={invalid && !value.document_type}
					data-invalid={!!errors?.document_type}
				>
					<FieldLabel htmlFor={`${prefix}document_type`}>
						Typ dokladu
					</FieldLabel>
					<Select
						value={value.document_type || ""}
						onValueChange={(documentType) => {
							onClearError?.("document_type");
							onChange({
								...value,
								document_type: documentType || null,
							});
						}}
						items={[
							{ value: "", label: "Vyberte typ" },
							...DOCUMENT_TYPES.map((type) => ({
								value: type.value,
								label: type.label,
							})),
						]}
					>
						<SelectTrigger
							className="w-full"
							data-warning={invalid && !value.document_type}
						>
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							{DOCUMENT_TYPES.map((type) => (
								<SelectItem key={type.value} value={type.value}>
									{type.label}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
					<FieldError>{errors?.document_type}</FieldError>
				</Field>
				<Field
					data-warning={invalid && !value.document_number}
					data-invalid={!!errors?.document_number}
				>
					<FieldLabel htmlFor={`${prefix}document_number`}>
						Číslo dokladu
					</FieldLabel>
					<Input
						id={`${prefix}document_number`}
						value={value.document_number || ""}
						onChange={(e) => {
							onClearError?.("document_number");
							onChange({
								...value,
								document_number: e.target.value || null,
							});
						}}
						placeholder="123456789"
						data-warning={invalid && !value.document_number}
						aria-invalid={!!errors?.document_number}
					/>
					<FieldError>{errors?.document_number}</FieldError>
				</Field>
			</div>
			<div className="grid grid-cols-2 gap-4">
				<Field
					data-invalid={expiryExpired || !!errors?.expiry_date}
					data-warning={
						!expiryExpired &&
						!errors?.expiry_date &&
						((invalid && !value.expiry_date) || expiryWarning)
					}
				>
					<FieldLabel htmlFor={`${prefix}expiry_date`}>Platnost do</FieldLabel>
					<DatePicker
						id={`${prefix}expiry_date`}
						value={value.expiry_date}
						onChange={(expiryDate) => {
							onClearError?.("expiry_date");
							onChange({
								...value,
								expiry_date: expiryDate,
							});
						}}
						data-invalid={expiryExpired || !!errors?.expiry_date}
						data-warning={
							!expiryExpired &&
							!errors?.expiry_date &&
							((invalid && !value.expiry_date) || expiryWarning)
						}
					/>
					<FieldError>{errors?.expiry_date}</FieldError>
				</Field>
				<Field
					data-warning={invalid && !value.issuer}
					data-invalid={!!errors?.issuer}
				>
					<FieldLabel htmlFor={`${prefix}issuer`}>Vydal</FieldLabel>
					<Input
						id={`${prefix}issuer`}
						value={value.issuer || ""}
						onChange={(e) => {
							onClearError?.("issuer");
							onChange({
								...value,
								issuer: e.target.value || null,
							});
						}}
						placeholder="MěÚ Praha"
						data-warning={invalid && !value.issuer}
						aria-invalid={!!errors?.issuer}
					/>
					<FieldError>{errors?.issuer}</FieldError>
				</Field>
			</div>
		</FieldGroup>
	);
}

export {
	DocumentFieldGroup,
	DOCUMENT_TYPES,
	type Document,
	type DocumentErrors,
	type DocumentFieldGroupProps,
};
